import styled from "@emotion/styled";
import { Box, Grid, IconButton, Table, Tooltip } from "@mui/material";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { ReactComponent as ArrowDown } from "assets/ArrowDown.svg";
import { ReactComponent as ArrowUp } from "assets/ArrowUp.svg";
import Warning from "assets/noDataWarning.png";
import { ReactComponent as SortArrowDown } from "assets/SortArrowDown.svg";
import { ReactComponent as SortArrowUp } from "assets/SortArrowUp.svg";
import { ReactComponent as SortIcon } from "assets/SortIcon.svg";

import { ReactComponent as TableTooltip } from "assets/TableTooltip.svg";
import { setTableHelperData } from "components/app/App.slice";
import ThemeContext from "components/app/Context";
import { setSelectedUserList } from "components/features/Corp/CorpUser.Slice";
import { uniqueId } from "lodash";
import { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { setSorting } from "redux/sorting.slice";
import { tokens } from "styles/Tokens";
import { nestedTernaryCheckerCond1 } from "utils/CommonChecker";
import { sortableProperties } from "utils/CommonConstants";
import { getTableColor } from "utils/CommonFunctions";
import Button from "./Button";
import CheckBox from "./Checkbox";
import MobileCommonTable from "./MobileCommonTable";
import Scroller from "./Scroller/InfiniteScroller";
import SCRow from "./SCTable";
import "./Table.scss";
import Typography from "./Typography";

export const StyledLink = styled(Link)`
  font-family: Noto Sans;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  text-decoration: none;
  color: ${tokens.Text.General.PrimaryHeadline};
`;

function Row(props: any) {
  const { row } = props;
  const [isExpanded, setIsExpanded] = useState(false);
  const isTooltipInTable = row?.data?.find((item: any) => !!item.tooltip);
  const subClasses = row?.data?.find((item: any) => item.hasOwnProperty("subClasses"))?.subClasses;

  const renderList = (item: any, isExpanded: boolean) => {
    return isExpanded && item.tooltip?.list?.length && item.content !== 0
      ? item?.tooltip?.list?.map((item: any) => {
          return (
            <Grid
              key={uniqueId("TOOLTIP_ITEM_")}
              sx={{ paddingY: "4px", color: getTableColor(item?.name), whiteSpace: "nowrap" }}
            >
              <Typography variant={"body2"} component={"span"} showBold>
                {item.value < 10 ? "0" : ""}
                {`${item.value ?? ""}  `}
              </Typography>
              {item?.name} {item?.isShowStar > 0 ? "*" : ""}
            </Grid>
          );
        })
      : item.content ?? "-";
  };

  return (
    <TableRow key={row.id} sx={{ "&:last-child td": { border: 0 } }} className={`${subClasses} table-row-common`}>
      {isTooltipInTable && (
        <TableCell
          component="td"
          scope="row"
          sx={{
            padding: "8px 0 8px 8px",
            width: "12px",
            color: tokens.Text.General.PrimaryHeadline,
            cursor: "pointer"
          }}
        >
          <IconButton onClick={() => setIsExpanded(!isExpanded)}>{isExpanded ? <ArrowUp /> : <ArrowDown />}</IconButton>
        </TableCell>
      )}
      {row?.data?.map((item: any) => {
        return (
          <TableCell
            key={uniqueId("TABLECELL_")}
            component="td"
            scope="row"
            sx={{
              padding: "8px 16px",
              color: tokens.Text.General.PrimaryHeadline
            }}
          >
            {item?.to ? (
              <StyledLink to={`${item?.to}`}>{renderList(item, isExpanded)}</StyledLink>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  gap: "4px",
                  alignItems: "center",
                  justifyContent: item?.contentAlignment ?? "flex-start"
                }}
              >
                {item?.icon && <item.icon />}
                <Typography component={"div"} variant={"body2"} showBold={item.showBold} color={item.color}>
                  {renderList(item, isExpanded)}
                </Typography>
              </Box>
            )}
          </TableCell>
        );
      })}
    </TableRow>
  );
}

export default function BasicTable(props: any) {
  const { isMobileScreen } = useContext(ThemeContext);

  const dispatch = useAppDispatch();
  const {
    isTablePaginated = true,
    noDataMessage = "No Results",
    scrollableTarget = "tableDiv",
    sx = {},
    handleUserVisible,
    fetchIndividualProperty,
    tableName,
    setUnCheckedMappings,
    unCheckedMappings,
    rowSelection,
    customPaginationType
  } = props;

  useEffect(() => {
    dispatch(setTableHelperData(props.data.rows?.length));
  }, [props.data.rows]);

  const sorting = useAppSelector((state) => state.sortingDetails.sorting);
  const recentSortingApplied = sorting?.[tableName];

  const fetchMore = () => {
    props?.pagination?.setPageNo(props?.pagination?.pageNo + 1);
  };

  const getSortIcon = useCallback(
    (item: any) => {
      const cond1 =
        recentSortingApplied && Object.keys(recentSortingApplied).length > 0
          ? item?.key === recentSortingApplied?.property ||
            sortableProperties[item?.content] === recentSortingApplied?.property
          : false;
      const cond2 = recentSortingApplied?.order === "asc";
      return (
        <div
          style={{ display: "flex", alignSelf: "center" }}
          className={props?.data?.rows?.length > 0 ? "cursor-pointer" : ""}
          onClick={() => {
            if (props?.data?.rows?.length) {
              if (props.handleSorting) props.handleSorting(item.content);
              else dispatch(setSorting({ tableName: tableName, header: item.content, key: item.key }));
            }
          }}
        >
          {nestedTernaryCheckerCond1(cond1, cond2, <SortArrowUp />, <SortArrowDown />, <SortIcon />)}
        </div>
      );
    },
    [props?.data?.rows?.length, props.handleSorting, recentSortingApplied]
  );
  const wrapper: any = document.getElementsByClassName("my-list-corp-right-wrapper")[0];
  const tableTotalWidth = wrapper?.offsetWidth;

  const noDataFound = () => {
    return (
      <TableRow>
        <TableCell colSpan={props?.data?.headers?.length}>
          <Stack spacing={3} alignItems="center" justifyContent="center" sx={{ padding: "40px 24px" }}>
            <img src={Warning} alt="No Data Warning" className="no-data-banner-icon" />
            <Typography variant="body1" color={tokens.Text.General.Tertiary}>
              {noDataMessage}
            </Typography>
          </Stack>
        </TableCell>
      </TableRow>
    );
  };

  const getScableContent = () => {
    if (props?.data?.listCategory && props?.view === "myList") {
      if (props?.data?.extraData?.length) {
        return props?.data?.extraData.map((row: any, index: any) => {
          return (
            <SCRow
              key={`sc-table-${row.propertyName}`}
              row={row}
              setId={props?.setId}
              handleUserVisible={handleUserVisible}
              index={index}
              fetchIndividualProperty={fetchIndividualProperty}
              handleParentChange={props.handleParentChange}
              handleChildChange={props.handleChildChange}
              allTeamMember={props.allTeamMember}
              selectedList={props.selectedList}
              setUnCheckedMappings={setUnCheckedMappings}
              unCheckedMappings={unCheckedMappings}
              rowSelection={rowSelection}
            />
          );
        });
      } else {
        return noDataFound();
      }
    }
    // else if (props?.data?.view === "manageSuggestions")
    else if (props?.innerTable) {
      if (props?.data?.rows?.length) {
        return props?.data?.rows?.map((row: any, index: any) => {
          const args: any = {
            row: row,
            index: index
          };
          if (isMobileScreen) {
            <></>;
          }
          return props?.innerTable(args);
        });
      } else return noDataFound();
    } else {
      if (props?.data?.rows?.length) {
        return props?.data?.rows?.map((row: any, index: any) => {
          return <Row key={row.name} row={row} />;
        });
      } else {
        return noDataFound();
      }
    }
  };

  const renderTable = () => {
    if (isMobileScreen) {
      if (props.data.listCategory) {
        return (
          <SCRow
            {...props}
            setId={props?.setId}
            setUnCheckedMappings={setUnCheckedMappings}
            unCheckedMappings={unCheckedMappings}
          />
        );
      }
      // if (props?.data?.view === "manageSuggestions")
      if (props?.innerTable) {
        const args = { ...props };
        return props?.innerTable(args);
      }
      return <MobileCommonTable {...props} />;
    }

    return MyTable;
  };

  const MyTable = (
    <TableContainer elevation={0} component={Paper} sx={sx} className="mytable-parent-common">
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
          <TableRow>
            {props?.data?.headers?.map((item: any) => {
              return (
                <TableCell key={uniqueId(item.id)}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    {item.isCheckBox && (
                      <CheckBox
                        disabled={props?.data?.view === "manageSuggestions" && props.totalRecords === 0}
                        onChange={props.handleSelectedAll}
                        checked={props.selectAllProperties}
                      />
                    )}

                    <Typography variant={"body2"} color={tokens.Text.General.PrimaryBody}>
                      {item.content}
                    </Typography>
                    {item?.icon && <item.icon />}
                    {item?.isSorting && getSortIcon(item)}
                    {item.tooltip && (
                      <>
                        <Tooltip title={item.tooltip} placement={item.tooltipDirection} arrow>
                          <TableTooltip />
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody sx={{ ".MuiTableCell-root": { borderBottom: "none" } }}>{getScableContent()}</TableBody>
      </Table>
    </TableContainer>
  );

  if (!isTablePaginated) {
    return renderTable();
  } else {
    return (
      <>
        <Scroller
          scrollableTarget={scrollableTarget}
          dataLength={props?.dataLength || props?.data?.rows?.length || 0}
          loadMore={fetchMore}
          totalList={props.totalRecords}
          label={props.customPaginationType ?? "records"}
          footerStyle={{ marginY: "16px" }}
        >
          {renderTable()}
        </Scroller>
        {props?.data?.listCategory && (props.selectAllProperties || rowSelection.length > 0) && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            sx={{
              position: "absolute",
              height: "94px",
              backgroundColor: tokens.Background.General.Primary,
              bottom: "-94px",
              right: "0px",
              width: tableTotalWidth,
              paddingLeft: "24px",
              paddingRight: "38px",
              borderTop: `1px solid ${tokens.Border.General.Tertiary}`
            }}
          >
            <div>
              <Button
                variant="secondary"
                label="Assign Plans"
                onClick={() => {
                  props.setMultiplansAssign(true);
                  dispatch(setSelectedUserList(props.allTeamMember));
                }}
              />
            </div>
          </Stack>
        )}
      </>
    );
  }
}
